import { ObjectValues } from 'common/tsHelpers';
import { z } from 'zod';
import { SimData } from 'src/common/components/Sim/constants/sim';
import { SubscriptionStatus } from 'src/common/components/SubscriptionStatus/constants/subscriptionStatus';
import { MNOStatusFilter } from 'src/common/components/Sim/constants/mnoStatus';

export const SIM_PROFILE_IDS = {
  ICCID: 'ICCID',
  IMSI: 'IMSI',
  MSISDN: 'MSISDN',
} as const;

export const NUMERIC_SIM_PROFILE_IDS = {
  ICCID: 0,
  IMSI: 1,
  MSISDN: 2,
} as const;

export const SESSION_STATUS = {
  UNKNOWN: 'Unknown',
  IN_SESSION: 'InSession',
  DISCONNECTED: 'Disconnected',
} as const;

export const SESSION_STATUS_NUMERIC = {
  DISCONNECTED: 0,
  IN_SESSION: 1,
} as const;

export const sessionStatusSchema = z.union([
  z.literal(SESSION_STATUS.UNKNOWN),
  z.literal(SESSION_STATUS.DISCONNECTED),
  z.literal(SESSION_STATUS.IN_SESSION),
]);

export const sessionStatusFilterSchema = sessionStatusSchema;

export const simIdSchema = z.union([
  z.literal(NUMERIC_SIM_PROFILE_IDS.ICCID),
  z.literal(NUMERIC_SIM_PROFILE_IDS.IMSI),
  z.literal(NUMERIC_SIM_PROFILE_IDS.MSISDN),
]);

export const sortingSchema = z.union([z.literal('asc'), z.literal('desc'), z.literal(null)]);

export const sortSchema = z.object({ columnName: z.string(), sort: sortingSchema });

export const accountReferenceFilterSchema = z.union([z.number(), z.literal('')]);

export const simInventoryExtededBodyFiltersSchema = z.object({
  pageSize: z.optional(z.number()),
  productCodes: z.optional(z.array(z.union([z.string(), z.null()]))),
  pageNumber: z.optional(z.number()),
  orderNumber: z.optional(z.string()),
  connectionId: z.optional(z.string()),
  cciis: z.optional(z.array(z.union([z.string(), z.null()]))),
  mnoNames: z.optional(z.array(z.union([z.string(), z.null()]))),
  label: z.optional(z.string()),
  tags: z.optional(z.array(z.string())),
  mnoStatus: z.optional(z.array(z.union([z.number(), z.null()]))),
  subscriptionStatus: z.optional(z.array(z.union([z.number(), z.null()]))),
  simProfileIds: z.optional(
    z.object({
      type: simIdSchema,
      ids: z.array(z.string()),
    }),
  ),
  sessionStatus: z.optional(
    z.array(
      z.union([
        z.literal(SESSION_STATUS_NUMERIC.IN_SESSION),
        z.literal(SESSION_STATUS_NUMERIC.DISCONNECTED),
        z.literal(''),
      ]),
    ),
  ),
  accountName: z.optional(z.string()),
  accountRef: z.optional(z.array(z.union([z.number(), z.null()]))),
  order: z.optional(z.string()),
  iccid: z.optional(z.string()),
  imsi: z.optional(z.string()),
  msisdn: z.optional(z.string()),
  simProfileIdsRange: z.optional(
    z.object({
      type: simIdSchema,
      idsFrom: z.string(),
      idsTo: z.string(),
    }),
  ),
  customFieldSearch: z.optional(
    z.array(
      z.object({
        id: z.string(),
        textValue: z.optional(z.string()),
        optionIds: z.optional(z.array(z.string())),
      }),
    ),
  ),
});

export const simInventoryFiltersSchema = z.object({
  accounts: z.array(z.string()),
  sessionStatus: sessionStatusFilterSchema,
  orderNumber: z.string(),
  connectionId: z.string(),
  mobileNetworkOperator: z.array(z.string()),
  tags: z.array(z.string()),
  iccidFrom: z.string(),
  iccidTo: z.string(),
  customFields: z.any(), //(z.string()), //  'customFieldId1-type-value1,value2,value3;customFieldId2-type-value1,value2' //type: text/dropdown
});

export type SimProfileId = ObjectValues<typeof SIM_PROFILE_IDS>;

export type SessionStatusFilter = ObjectValues<typeof SESSION_STATUS>;

export type AccountReferenceFilter = z.infer<typeof accountReferenceFilterSchema>;

export type SimInventoryExtededFilterBody = z.infer<typeof simInventoryExtededBodyFiltersSchema>;

export type SimInventoryFilters = z.infer<typeof simInventoryFiltersSchema>;

export type SimInventorySort = z.infer<typeof sortSchema>;

export type SimInventoryExtededFilterBodyIdsProps = keyof Pick<
  SimInventoryExtededFilterBody,
  'iccid' | 'imsi' | 'msisdn'
>;

export type SimInventoryColumnsNames = keyof SimData;

type BlankableSearchFilter<T extends string | number = string> = T | null;

type CustomFieldValue = string | string[];

export type CustomFields = Record<string, CustomFieldValue>;

export type SimInventoryFiltersV2 = {
  searchText: string;
  chosenIdType: SimProfileId;
  iccidFrom: string;
  iccidTo: string;
  orderNumber: string;
  connectionId: string;
  accounts: BlankableSearchFilter[];
  productCodes: BlankableSearchFilter[];
  mobileNetworkOperator: BlankableSearchFilter[];
  mnoNames: string[];
  customFields: CustomFields;
  subscriptionStatus: SubscriptionStatus[];
  mnoStatus: MNOStatusFilter[];
  tags: string[];
  sessionStatus: SessionStatusFilter;
  mobileNetworkOperatorId: BlankableSearchFilter<number>[];
};

export type SimInventorySearchParamsV2 = {
  searchText: string[];
  chosenIdType: SimProfileId;
  accounts: (string | null)[];
  productCodes: (string | null)[];
  sessionStatus: SessionStatusFilter;
  orderNumber: string;
  connectionId: string;
  mobileNetworkOperator: (string | null)[];
  mnoNames: string[];
  tags: string[];
  iccidFrom: string;
  iccidTo: string;
  mnoStatus: MNOStatusFilter[];
  subscriptionStatus: SubscriptionStatus[];
  customFields?: CustomFields;
};
